import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Sparkles, Phone, Users, Calendar, MessageSquare, Settings, X, ChevronLeft, ChevronRight } from 'lucide-react';

const TOUR_KEY = 'nextbell-dashboard-tour-v1';

// Tour content rendered as a step-by-step modal carousel. We deliberately do
// NOT anchor to sidebar elements — mobile users can't see the sidebar, so a
// spotlight tour breaks. A standalone carousel works on every screen size.
const STEPS = [
  {
    title: 'Calls',
    content: 'Every call Ava handles lands here with a transcript, AI summary, and outcome. No manual logging.',
    icon: Phone,
  },
  {
    title: 'Leads',
    content: 'Each new caller becomes a lead with a score. Ava asks the right intake questions for your industry.',
    icon: Users,
  },
  {
    title: 'Appointments',
    content: 'Bookings appear here in real time. Ava sends a 24h SMS reminder and a follow-up after every visit.',
    icon: Calendar,
  },
  {
    title: 'Messaging',
    content: 'Two-way SMS with leads. Ava drafts replies, you approve.',
    icon: MessageSquare,
  },
  {
    title: 'Settings & Billing',
    content: "Plan, billing, your business hours, and Ava's knowledge live here.",
    icon: Settings,
  },
];

export default function DashboardTour() {
  const { user } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const [stepIndex, setStepIndex] = useState(-1);  // -1 = welcome, 0..STEPS.length-1 = walkthrough

  useEffect(() => {
    if (!user?.business?.id) return undefined;
    // Don't auto-show the welcome modal on deep-link routes (settings
    // deep-links, etc.) — only on the dashboard home. Users who land
    // straight on a deep route from a marketing or test link should
    // be able to interact immediately. They can replay the tour from the
    // sidebar "Onboarding" button at any time.
    const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
    const isDashboardHome = path === '/dashboard' || path === '/dashboard/';
    if (!isDashboardHome) return undefined;
    const seen = localStorage.getItem(`${TOUR_KEY}-${user.business.id}`);
    if (!seen) {
      const t = setTimeout(() => { setShowWelcome(true); setStepIndex(-1); }, 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [user]);

  useEffect(() => {
    const handler = () => { setShowWelcome(true); setStepIndex(-1); };
    window.addEventListener('nextbell:start-tour', handler);
    return () => window.removeEventListener('nextbell:start-tour', handler);
  }, []);

  const markDone = () => {
    if (user?.business?.id) {
      localStorage.setItem(`${TOUR_KEY}-${user.business.id}`, '1');
    }
  };

  const closeTour = () => {
    setShowWelcome(false);
    setStepIndex(-1);
    markDone();
  };

  if (!showWelcome) return null;

  const onWelcome = stepIndex === -1;
  const current = onWelcome ? null : STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="dashboard-welcome-modal">
      <div className="relative bg-white dark:bg-[#1a1a1c] dark:border dark:border-white/10 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        <button onClick={closeTour} aria-label="Close" data-testid="tour-close-btn"
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:text-slate-500 dark:hover:text-slate-200 z-10 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-white/[0.05]">
          <X className="w-5 h-5" />
        </button>

        {onWelcome ? (
          <div className="p-6 sm:p-8">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(59,130,246,0.45)]">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-gray-900 dark:text-white mb-2">Welcome to Nextbell</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">
              Ava is live and ready to take calls. Want a 30-second tour of your new dashboard?
            </p>
            <ul className="space-y-2.5 mb-6">
              {STEPS.map((s) => {
                const Icon = s.icon;
                return (
                  <li key={s.title} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{s.title}</p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">{s.content}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center gap-2">
              <button onClick={closeTour} data-testid="tour-skip-btn"
                className="flex-1 bg-gray-100 dark:bg-white/[0.05] hover:bg-gray-200 dark:hover:bg-white/[0.08] text-gray-700 dark:text-slate-200 text-sm font-medium rounded-full py-2.5">
                Skip for now
              </button>
              <button onClick={() => setStepIndex(0)} data-testid="tour-start-btn"
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-full py-2.5">
                Take the tour
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 sm:p-8" data-testid={`tour-step-${stepIndex}`}>
            {/* Step counter */}
            <div className="flex items-center gap-1.5 mb-5">
              {STEPS.map((_, i) => (
                <span key={i} className={`h-1 rounded-full transition-all ${i === stepIndex ? 'w-6 bg-blue-600' : 'w-1.5 bg-gray-200 dark:bg-white/10'}`} />
              ))}
            </div>
            {/* Icon + title */}
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(59,130,246,0.45)]">
              {current && <current.icon className="w-6 h-6 text-white" />}
            </div>
            <h2 className="font-heading text-2xl font-semibold text-gray-900 dark:text-white mb-2">{current?.title}</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-7 leading-relaxed">{current?.content}</p>
            {/* Footer nav */}
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => setStepIndex(stepIndex - 1)}
                disabled={stepIndex === 0}
                data-testid="tour-back-btn"
                className="flex items-center gap-1 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <span className="text-xs text-gray-400 dark:text-slate-500 tabular-nums">{stepIndex + 1} / {STEPS.length}</span>
              {isLast ? (
                <button onClick={closeTour} data-testid="tour-done-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-full px-5 py-2">
                  Got it
                </button>
              ) : (
                <button onClick={() => setStepIndex(stepIndex + 1)} data-testid="tour-next-btn"
                  className="flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-full px-4 py-2">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
